import React, { useEffect, useRef, useState } from 'react';
import './css/index.scss';
import { useSelector } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import PageTitle from '../../components/PageTitle';
import MyButton from '../../components/MyButton';
import AddPhotosIcon from '../../assets/formIcons/addPhotos.svg';
import TemplatePhotosInfoModal from './components/TemplatePhotosInfoModal';
import { TEMPLATE_PHOTOS_MODAL_STATES } from './constants';
import getFormData from '../../helpers/getFormData';
import sendRequest from '../../helpers/sendRequest';
import DeletePhotoIcon from '../../assets/formIcons/deletePhoto.svg';
import CopyPhotoIcon from '../../assets/formIcons/copyPhoto.svg';
import Api from '../../Api';

import 'react-toastify/dist/ReactToastify.min.css';
import LocalStorageServices from '../../helpers/LocalStorageServices';
import MyToggle from '../../components/MyToggle';
import SubCategoriesTable from '../../components/SubCategoriesTable';

function TemplatePhotos(props) {
  const selectPhotosInput = useRef();
  const { systemCountry } = useSelector((store) => store.account);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalState, setModalState] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState({});
  const [selectedSubCategory, setSelectedSubCategory] = useState({});
  const [addingLoading, setAddingLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [workingWithOnlineCategories, setWorkingWithOnlineCategories] = useState(Boolean(LocalStorageServices.getIsWorkingWithOnlineCategories()));

  const { categories } = useSelector((store) => store.categories);

  const onAddClick = () => {
    if (typeof selectPhotosInput.current?.click === 'function') {
      selectPhotosInput.current.click();
    }
  };

  const onPhotosSelect = async (ev) => {
    const { files: filesList } = ev.target;
    const files = Array.from(filesList);
    if (!filesList.length) return;

    if (files.length > 15) {
      setModalState(TEMPLATE_PHOTOS_MODAL_STATES.maxCountError);
      setIsModalVisible(true);
    }

    const payload = {
      category_id: selectedSubCategory.category_id,
    };

    if (!selectedSubCategory?._id?.startsWith('all')) {
      payload.sub_category_id = selectedSubCategory._id;
    }

    const formData = getFormData(payload, files.slice(0, 15));

    const data = await sendRequest({
      request: Api.addPhotosToTemplatePhotos,
      payload: formData,
      warnErrorText: 'while adding photos to template photos',
      setLoading: setAddingLoading,
    });

    if (data?.status !== 'ok') return;

    setPhotos((prev) => [...data.templatePhotos, ...prev]);
  };

  const onCategoryClick = async (val) => {
    setSelectedSubCategory(val);

    const payload = {
      category_id: val.category_id,
    };

    if (!val?._id?.startsWith('all')) {
      payload.sub_category_id = val._id;
    }

    const data = await sendRequest({
      request: Api.getTemplatePhotosPhotos,
      payload,
      warnErrorText: 'while getting template photos',
    });

    if (data?.status !== 'ok') return;

    setPhotos(data.templatePhotos);
  };

  const onPhotoItemClick = (photo) => () => {
    setSelectedPhoto(photo);
    setModalState(TEMPLATE_PHOTOS_MODAL_STATES.viewPhoto);
    setIsModalVisible(true);
  };

  const onDeletePhotoClick = (photo) => (ev) => {
    ev.stopPropagation();
    setSelectedPhoto(photo);
    setModalState(TEMPLATE_PHOTOS_MODAL_STATES.delete);
    setIsModalVisible(true);
  };

  const onCopyPhotoClick = (photo) => (ev) => {
    ev.stopPropagation();
    toast.success('Id фото был скопирован в буфер обмена');
    navigator.clipboard.writeText(photo._id);
  };

  const handleIsWorkingWithOnlineCategoriesToggle = () => {
    const newVal = !workingWithOnlineCategories;
    setWorkingWithOnlineCategories(newVal);
    LocalStorageServices.setIsWorkingWithOnlineCategories(newVal);
    setSelectedSubCategory(null);
  };

  useEffect(() => {
    if (categories?.length) {
      onCategoryClick(categories[0]).then();
    }
  }, [categories]);

  return (
    <div className="templatePhotos_wrapper">
      <PageTitle
        text={`Шаблонные фото ${workingWithOnlineCategories ? 'для онлайн категорий' : 'для категорий'} (независимо от страны)`}
        RightComponent={(
          <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
            <MyButton
              text="Добавить"
              svgPath={AddPhotosIcon}
              onClick={onAddClick}
              className="templatePhotos_add_btn"
              loading={addingLoading}
            />
            <input
              ref={selectPhotosInput}
              type="file"
              multiple
              accept="image/png, image/jpeg"
              className="templatePhotos_select_photos"
              onChange={onPhotosSelect}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <p>Работа с онлайн категориями</p>
              <MyToggle value={workingWithOnlineCategories} onClick={handleIsWorkingWithOnlineCategoriesToggle} />
            </div>
          </div>
        )}
      />

      <div className="templatePhotos_container">
        <div className="templatePhotos_left">
          <SubCategoriesTable
            selectedSubCategory={selectedSubCategory}
            onClick={onCategoryClick}
            isOnline={workingWithOnlineCategories}
          />
        </div>
        <div className="templatePhotos_table">
          {photos.length ? (
            photos.map((photo) => (
              <div
                key={photo._id}
                className="templatePhotos_table_item"
                onClick={onPhotoItemClick(photo)}
              >

                <img
                  src={Api.url + photo.photo?.preview_url}
                  alt=""
                  className="templatePhotos_table_item_photo"
                />

                <span className="templatePhotos_table_item_title">{photo.photo?.title || 'No title'}</span>

                <div className="templatePhotos_item_overlay">
                  <div
                    className="templatePhotos_item_overlay_icon"
                    onClick={onDeletePhotoClick(photo)}
                  >
                    <img src={DeletePhotoIcon} alt="" />
                  </div>
                  <div className="templatePhotos_item_overlay_icon" onClick={onCopyPhotoClick(photo)}>
                    <img src={CopyPhotoIcon} alt="" />
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="templatePhotos_no_photo">Нет фото в этой папке</p>
          )}
        </div>
      </div>

      <TemplatePhotosInfoModal
        isVisible={isModalVisible}
        setIsVisible={setIsModalVisible}
        modalState={modalState}
        setPhotos={setPhotos}
        selectedPhoto={selectedPhoto}
        setSelectedPhoto={setSelectedPhoto}
        systemCountry={systemCountry}
      />

      <ToastContainer pauseOnHover={false} autoClose={2000} />
    </div>
  );
}

export default TemplatePhotos;
